import { Col, Divider, Input, Row, Text } from 'components';
import { theme } from 'lib';
import { FiX } from 'react-icons/fi';
import styled from 'styled-components';
import { DURATION_UNITS } from '../constants';

// Shared presentational pieces for the FanWave admin screen. Kept in one place so the
// global / per-channel / per-match sections look identical.

// A titled card. `right` renders an optional action/badge slot in the header.
export const Section = ({ title, description, children, right, icon }) => (
  <Col
    noFlex
    wid="100%"
    pad="20px 22px"
    style={{
      backgroundColor: '#fff',
      borderRadius: '14px',
      border: `1px solid ${theme.GREY_20}`,
      boxShadow: '0 1px 2px rgba(20, 28, 40, 0.04)'
    }}
  >
    <Row center between noFlex wid="100%" marg={description ? '0 0 2px' : '0 0 14px'}>
      <Row center noFlex gap="9px">
        {icon}
        <Text h4 bold marg="0">
          {title}
        </Text>
      </Row>
      {right}
    </Row>
    {description && (
      <Text c1 col={theme.GREY_60} marg="0 0 16px" style={{ fontSize: '12.5px', lineHeight: 1.45 }}>
        {description}
      </Text>
    )}
    {children}
  </Col>
);

// Tap targets are positive integers, so keep the field strictly digit-only: block the
// characters `type="number"` otherwise allows (e / + / - / .) and strip anything non-numeric
// that slips in via paste.
export const blockNonInteger = (e) => {
  if (['e', 'E', '+', '-', '.', ','].includes(e.key)) e.preventDefault();
};
export const toDigits = (raw) => (raw == null ? '' : String(raw).replace(/[^0-9]/g, ''));

export const NumberField = ({ label, description, value, onChange, step, error, disabled }) => (
  <Col noFlex style={{ flex: '1 1 160px', minWidth: '140px', maxWidth: '240px' }}>
    <Text b2 bold marg="0 0 5px" style={{ fontSize: '13px' }}>
      {label}
    </Text>
    <Input
      type="number"
      inputMode="numeric"
      value={value}
      onChange={(e) => onChange(toDigits(e.target.value))}
      onKeyDown={blockNonInteger}
      placeholder={label}
      step={step}
      disabled={disabled}
    />
    {error ? (
      <Text c1 col={theme.SECONDARY} marg="5px 0 0" style={{ fontSize: '11px' }}>
        {error}
      </Text>
    ) : (
      description && (
        <Text c1 col={theme.GREY_60} marg="5px 0 0" style={{ fontSize: '11px' }}>
          {description}
        </Text>
      )
    )}
  </Col>
);

// Native select sized to sit flush beside `Input` — same 43px height, pill radius and GREY_50
// border as DefaultInput. Deliberately not the shared `Dropdown`, which is a 480px-wide overlay
// built for filter bars and far too heavy for a three-option unit toggle next to a number.
const UnitSelect = styled.select`
  height: 43px;
  width: 118px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 50px;
  border: 1px solid ${(props) => (props.error ? theme.SECONDARY : theme.GREY_50)};
  background-color: transparent;
  font-family: CeraPro;
  font-size: 15px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #000;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

/**
 * A duration entered as a number PLUS its unit (Minutes / Hours / Days). `value`/`unit` are the
 * raw form state; the caller converts to minutes with `durationToMinutes`, because minutes are
 * what the API stores.
 *
 * The unit exists purely for legibility: a 3-day window entered as "3" + "Days" is checkable at
 * a glance, where the equivalent "4320" is not. Unit is caller-owned state rather than derived
 * per render, so switching it re-labels the number already typed (3 minutes → 3 days) instead of
 * silently rewriting what the admin entered.
 */
export const DurationField = ({
  label,
  description,
  value,
  unit,
  onChange,
  onUnitChange,
  error,
  disabled
}) => (
  <Col noFlex style={{ flex: '1 1 300px', minWidth: '270px', maxWidth: '380px' }}>
    <Text b2 bold marg="0 0 5px" style={{ fontSize: '13px' }}>
      {label}
    </Text>
    <Row center noFlex gap="8px" wid="100%">
      <Input
        type="number"
        inputMode="numeric"
        wid="calc(100% - 126px)"
        value={value}
        onChange={(e) => onChange(toDigits(e.target.value))}
        onKeyDown={blockNonInteger}
        placeholder="0"
        disabled={disabled}
        error={!!error}
      />
      <UnitSelect
        value={unit}
        error={!!error}
        disabled={disabled}
        onChange={(e) => onUnitChange(e.target.value)}
        aria-label={`${label} unit`}
      >
        {DURATION_UNITS.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </UnitSelect>
    </Row>
    {error ? (
      <Text c1 col={theme.SECONDARY} marg="5px 0 0" style={{ fontSize: '11px' }}>
        {error}
      </Text>
    ) : (
      description && (
        <Text c1 col={theme.GREY_60} marg="5px 0 0" style={{ fontSize: '11px' }}>
          {description}
        </Text>
      )
    )}
  </Col>
);

// A small "label: value" line — kept for any compact readouts that still want it.
export const InfoRow = ({ label, value, highlight }) => (
  <Row center between noFlex wid="100%" pad="3px 0">
    <Text b2 col={theme.GREY_60} style={{ fontSize: '12px' }}>
      {label}
    </Text>
    <Text b2 bold col={highlight ? theme.SECONDARY : theme.GREY_90} style={{ fontSize: '12px' }}>
      {value}
    </Text>
  </Row>
);

const BADGE_VARIANTS = {
  neutral: { bg: theme.GREY_10, col: theme.GREY_70, border: theme.GREY_20, dot: theme.GREY_40 },
  locked: {
    bg: theme.SECONDARY_10,
    col: theme.SECONDARY,
    border: theme.SECONDARY_20,
    dot: theme.SECONDARY
  },
  active: { bg: '#E7FBF3', col: '#0C9C74', border: '#C7F4E5', dot: theme.GREEN },
  muted: { bg: theme.GREY_10, col: theme.GREY_50, border: theme.GREY_20, dot: theme.GREY_30 },
  info: { bg: '#EAF2FE', col: '#2563EB', border: '#D3E3FC', dot: '#2563EB' },
  danger: { bg: '#FDECEC', col: '#DC2626', border: '#F8D4D4', dot: '#DC2626' },
  warning: { bg: '#FFF4E5', col: '#B45309', border: '#FCE4C4', dot: '#D97706' }
};

/**
 * The accent that says "these belong to one match" — the same blue the `info` badge and the form's
 * callouts are drawn in, shared by the list's group panel and the create form's match note so both
 * surfaces read as the same idea.
 *
 * Three strengths, because they sit on different grounds: `panel` goes behind white cards and has to
 * stay quieter than they are, `chip` is a filled marker on white, and `rail`/`border` draw the edges.
 */
export const MATCH_ACCENT = {
  rail: '#2563EB',
  /** Lighter than `chip`, so the white cards — and the pills — on top of it stay legible. */
  panel: '#F7FAFE',
  border: '#D3E3FC',
  chip: '#EAF2FE'
};

/** Wave lifecycle status → Badge variant (scheduled/active/completed/cancelled/expired). */
export const STATUS_BADGE_VARIANT = {
  scheduled: 'info',
  active: 'active',
  completed: 'muted',
  cancelled: 'danger',
  expired: 'warning'
};

// Status pill (Locked / Unlocked / Platform-wide …). `dot` shows a leading status dot.
export const Badge = ({ children, variant = 'neutral', dot = false }) => {
  const s = BADGE_VARIANTS[variant] || BADGE_VARIANTS.neutral;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 11px',
        borderRadius: '20px',
        fontSize: '11.5px',
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        background: s.bg,
        color: s.col,
        border: `1px solid ${s.border}`,
        fontFamily: 'CeraPro'
      }}
    >
      {dot && (
        <span
          style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }}
        />
      )}
      {children}
    </span>
  );
};

// Sidepanel header with a clear X close affordance (ContentHeader's 12px back-chevron is
// easy to miss). The panels also close on backdrop click via Sidepanel's closeModal.
export const PanelHeader = ({ text, onClose }) => (
  <Col pos="sticky" bg="#fff" top="0" zIndex={2} noFlex wid="100%">
    <Row center between noFlex wid="100%" pad="14px 24px 0">
      <Text h3 marg="0">
        {text}
      </Text>
      <button
        type="button"
        onClick={onClose}
        title="Close"
        style={{
          width: 34,
          height: 34,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${theme.GREY_20}`,
          borderRadius: '50%',
          background: '#fff',
          color: theme.GREY_70,
          cursor: 'pointer',
          flexShrink: 0
        }}
      >
        <FiX size={17} />
      </button>
    </Row>
    <Divider marg="12px 0 0" />
  </Col>
);

// Purple pill toggle used across the FanWave admin (Switch in `components` is green and
// sized for the app shell; the FanWave sections keep the SECONDARY accent).
export const Toggle = ({ on, onToggle, disabled }) => (
  <div
    onClick={disabled ? undefined : onToggle}
    style={{
      position: 'relative',
      width: '44px',
      height: '24px',
      borderRadius: '12px',
      backgroundColor: on ? theme.SECONDARY : theme.GREY_30,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'background-color 0.2s',
      flexShrink: 0
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: '3px',
        left: on ? '23px' : '3px',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
      }}
    />
  </div>
);

// Labelled radio option (the shared Radio component is styled for dark backgrounds).
export const RadioOption = ({ checked, onSelect, label, description, disabled }) => (
  <Row
    noFlex
    start
    gap="10px"
    onClick={disabled ? undefined : onSelect}
    style={{
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      alignItems: 'flex-start'
    }}
  >
    <span
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `2px solid ${checked ? theme.SECONDARY : theme.GREY_40}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 1,
        background: '#fff'
      }}
    >
      {checked && (
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: theme.SECONDARY }} />
      )}
    </span>
    <Col noFlex style={{ minWidth: 0 }}>
      <Text b2 bold={checked} marg="0" style={{ fontSize: '13px', lineHeight: 1.3 }}>
        {label}
      </Text>
      {description && (
        <Text c1 col={theme.GREY_60} marg="2px 0 0" style={{ fontSize: '11px', lineHeight: 1.4 }}>
          {description}
        </Text>
      )}
    </Col>
  </Row>
);

// Toggle + title + description header used by the toggleable sections (4, 6, 7, 8).
export const ToggleHeader = ({ title, description, on, onToggle, disabled }) => (
  <Row center between noFlex wid="100%">
    <Col noFlex style={{ minWidth: 0 }}>
      <Text b2 bold marg="0" style={{ fontSize: '13.5px' }}>
        {title}
      </Text>
      {description && (
        <Text c1 col={theme.GREY_60} marg="2px 0 0" style={{ fontSize: '11.5px' }}>
          {description}
        </Text>
      )}
    </Col>
    <Toggle on={on} onToggle={onToggle} disabled={disabled} />
  </Row>
);

// One source in the override-precedence chain (Global → Team → Override). The winning
// source is highlighted; unset sources are dimmed to a dash.
export const SourceChip = ({ label, value, winning, unset }) => (
  <Col
    noFlex
    pad="9px 14px"
    style={{
      borderRadius: '11px',
      minWidth: '96px',
      background: winning ? theme.SECONDARY_10 : theme.GREY_10,
      border: `1px solid ${winning ? theme.SECONDARY_20 : theme.GREY_20}`
    }}
  >
    <Text
      marg="0"
      style={{
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: winning ? theme.SECONDARY : theme.GREY_50
      }}
    >
      {label}
    </Text>
    <Text
      marg="3px 0 0"
      style={{
        fontSize: '17px',
        fontWeight: 700,
        color: unset ? theme.GREY_40 : theme.GREY_90
      }}
    >
      {unset ? '—' : value}
    </Text>
  </Col>
);

// The hero readout: big effective target + the resolved source chips + lock badge.
export const ResolvedTarget = ({ tapTarget, source = {}, locked }) => {
  const winning =
    source.p4pTarget != null ? 'override' : source.teamTarget != null ? 'team' : 'global';
  return (
    <Col
      noFlex
      wid="100%"
      pad="16px 18px"
      marg="0 0 16px"
      gap="14px"
      style={{
        background: theme.GREY_10,
        borderRadius: '12px',
        border: `1px solid ${theme.GREY_20}`
      }}
    >
      <Row center between gap="12px" wid="100%">
        <Col noFlex>
          <Text
            marg="0"
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: theme.GREY_50
            }}
          >
            Effective tap target
          </Text>
          <Text
            marg="2px 0 0"
            style={{ fontSize: '34px', fontWeight: 800, lineHeight: 1.05, color: theme.SECONDARY }}
          >
            {tapTarget}
          </Text>
        </Col>
        <Badge variant={locked ? 'locked' : 'neutral'} dot>
          {locked ? 'Locked' : 'Unlocked'}
        </Badge>
      </Row>

      <Row center start gap="8px" style={{ flexWrap: 'wrap' }}>
        <SourceChip
          label="Global"
          value={source.globalTarget}
          unset={source.globalTarget == null}
          winning={winning === 'global'}
        />
        <SourceChip
          label="Team"
          value={source.teamTarget}
          unset={source.teamTarget == null}
          winning={winning === 'team'}
        />
        <SourceChip
          label="Override"
          value={source.p4pTarget}
          unset={source.p4pTarget == null}
          winning={winning === 'override'}
        />
      </Row>
    </Col>
  );
};
